import { assign, sendParent, setup } from 'xstate';
import type { PublishScheduleDataInput, ScheduledBusResultsScheduleEntries } from '@/lib/gql/__generated__/graphql';
import type { ScheduleDataEvent } from './events';
import {
  publishScheduleDataActor,
  refreshScheduledBusesActor,
  viewScheduledBusesActor,
  type ScheduleDataReadResult,
} from './schedule-data.actors';

export interface ScheduleDataContext {
  entries: Array<ScheduledBusResultsScheduleEntries>;
  lastUpdatedAt: string | null;
  selectedRoute: string | null;
  selectedDirection: string | null;
  selectedStopId: string | null;
  error: unknown;
  publishInput: Omit<PublishScheduleDataInput, 'scheduleDataId'> | null;
}

const scheduleDataSetup = setup({
  types: {
    context: {} as ScheduleDataContext,
    events: {} as ScheduleDataEvent,
  },
  actors: {
    viewScheduledBusesActor,
    refreshScheduledBusesActor,
    publishScheduleDataActor,
  },
  actions: {
    storeReadResult: assign(({ event }) => {
      if (!('output' in event) || !isScheduleDataReadResult(event.output)) return {};
      const output = event.output;
      return {
        entries: output.entries,
        lastUpdatedAt: output.lastUpdatedAt,
        error: null,
      };
    }),
    storeError: assign({
      error: ({ event }) => ('error' in event ? event.error : new Error('The schedule data request failed.')),
    }),
    rememberPublishInput: assign(({ event }) => {
      if (event.type !== 'schedule-data.publish') return {};
      return { publishInput: event.input };
    }),
    clearError: assign({ error: null }),
  },
});

function isScheduleDataReadResult(value: unknown): value is ScheduleDataReadResult {
  if (typeof value !== 'object' || value === null) return false;
  if (!('entries' in value) || !Array.isArray(value.entries)) return false;
  return !('lastUpdatedAt' in value) || value.lastUpdatedAt === null || typeof value.lastUpdatedAt === 'string';
}

export const scheduleDataMachine = scheduleDataSetup.createMachine({
  id: 'schedule-data',
  initial: 'idle',
  context: {
    entries: [],
    lastUpdatedAt: null,
    selectedRoute: null,
    selectedDirection: null,
    selectedStopId: null,
    error: null,
    publishInput: null,
  },
  states: {
    idle: {
      on: {
        'schedule-data.load-view': { target: 'loading' },
        'schedule-data.refresh': { target: 'refreshing' },
        'schedule-data.publish': { target: 'publishing', actions: 'rememberPublishInput' },
      },
    },
    loading: {
      entry: 'clearError',
      invoke: {
        src: 'viewScheduledBusesActor',
        onDone: { target: 'ready', actions: 'storeReadResult' },
        onError: { target: 'error', actions: 'storeError' },
      },
      on: {
        'schedule-data.refresh': { target: 'refreshing' },
        'schedule-data.publish': { target: 'publishing', actions: 'rememberPublishInput' },
      },
    },
    refreshing: {
      entry: 'clearError',
      invoke: {
        src: 'refreshScheduledBusesActor',
        onDone: { target: 'ready', actions: 'storeReadResult' },
        onError: { target: 'error', actions: 'storeError' },
      },
      on: {
        'schedule-data.refresh': { target: 'refreshing' },
        'schedule-data.publish': { target: 'publishing', actions: 'rememberPublishInput' },
      },
    },
    ready: {
      on: {
        'schedule-data.load-view': { target: 'loading' },
        'schedule-data.refresh': { target: 'refreshing' },
        'schedule-data.publish': { target: 'publishing', actions: 'rememberPublishInput' },
      },
    },
    error: {
      on: {
        'schedule-data.load-view': { target: 'loading' },
        'schedule-data.refresh': { target: 'refreshing' },
        'schedule-data.publish': { target: 'publishing', actions: 'rememberPublishInput' },
      },
    },
    publishing: {
      invoke: {
        src: 'publishScheduleDataActor',
        input: ({ context }) =>
          context.publishInput ?? {
            sourceDocumentName: '',
            supportedRoutes: [],
            scheduleEntries: [],
          },
        onDone: {
          target: 'loading',
          actions: [
            sendParent({ type: 'schedule-data.published' }),
            sendParent({
              type: 'ui.toast.push',
              toast: { title: 'Schedule published', tone: 'success' },
            }),
          ],
        },
        onError: {
          target: 'error',
          actions: [
            'storeError',
            sendParent({
              type: 'ui.toast.push',
              toast: {
                title: 'Schedule could not be published',
                description: 'Check the schedule details and try again.',
                tone: 'error',
              },
            }),
          ],
        },
      },
      on: {
        'schedule-data.refresh': { target: 'refreshing' },
      },
    },
  },
});
